import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import moment from "moment";
import "../../Components/Visas/Requests.css";
import AdminTabs from "./AdminTabs";
import MarkupModal from "../../Components/Visas/MarkupModal";
import StatusModal from "../../Components/Visas/StatusModal";
import ApplicantForm from "../../Components/Visas/ApplicantForm.jsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RiCalendarLine } from "react-icons/ri";

const ClientsVisaRequests = () => {
  const { user, loading } = useAuth();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    visaReferenceNo: "",
    customerName: "",
    visaName: "",
    countryName: "",
    visaRequestStatus: "",
    paymentStatus: "",
    fromDate: "",
    toDate: "",
    companyId: "", // New filter for company
    supplierId: "", // New filter for supplier
  });
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [sendingToSupplier, setSendingToSupplier] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showNoApplicantsModal, setShowNoApplicantsModal] = useState(false);

  // New states for companies and suppliers dropdown
  const [companies, setCompanies] = useState([]);
  const [suppliersList, setSuppliersList] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [supplierLoading, setSupplierLoading] = useState(false);

  // New states for popups
  const [showMarkupPopup, setShowMarkupPopup] = useState(false);
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [selectedRequestForAction, setSelectedRequestForAction] =
    useState(null);

  const [showApplicantModal, setShowApplicantModal] = useState(false);
  const [selectedVisaRequest, setSelectedVisaRequest] = useState(null);
  const [applicantsList, setApplicantsList] = useState([]);

  // Custom input component for date picker
  const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className="input-group">
      <input
        type="text"
        className="form-control"
        value={value}
        onClick={onClick}
        ref={ref}
        readOnly
        placeholder="Select date"
      />
      <button className="btn btn-secondary" type="button" onClick={onClick}>
        <RiCalendarLine />
      </button>
    </div>
  ));

  const formatDate = (date) => {
    return moment(date).format("YYYY-MM-DD");
  };

  const getCurrentMonthDateRange = () => {
    const startOfMonth = moment().startOf("month");
    const endOfMonth = moment().endOf("month");

    return {
      fromDate: startOfMonth.toDate(),
      toDate: endOfMonth.toDate(),
    };
  };

  // Get companies from API
  const getCompanies = async () => {
    setCompanyLoading(true);
    try {
      const response = await apiService.getAll(
        `${config.GETALLCOMPANIES}?companyType=client`
      );
      const companiesData = response.data || response;
      setCompanies(companiesData);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setCompanyLoading(false);
    }
  };

  // Get suppliers from API
  const getSuppliersList = async () => {
    setSupplierLoading(true);
    try {
      const response = await apiService.getAll(
        `${config.GETALLCOMPANIES}?companyType=supplier`
      );
      const suppliersData = response.data || response;
      setSuppliersList(suppliersData);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    } finally {
      setSupplierLoading(false);
    }
  };

  // Main function to get visa requests
  const GetData = async (currentFilters, pageNumber) => {
    setIsLoading(true);
    try {
      let url = `${config.GETVISAREQUESTS}?PageNumber=${pageNumber}&PageSize=${pageSize}`;

      // Convert Date objects to string format for API
      const apiFilters = { ...currentFilters };
      if (apiFilters.fromDate instanceof Date) {
        apiFilters.fromDate = formatDate(apiFilters.fromDate);
      }
      if (apiFilters.toDate instanceof Date) {
        apiFilters.toDate = formatDate(apiFilters.toDate);
      }

      // Build query parameters
      const params = new URLSearchParams();

      // Add companyId if selected
      if (apiFilters.companyId && apiFilters.companyId !== "") {
        params.append("companyId", apiFilters.companyId);
      }

      // Add supplierId if selected
      if (apiFilters.supplierId && apiFilters.supplierId !== "") {
        params.append("supplierId", apiFilters.supplierId);
      }

      // Add other filters
      Object.keys(apiFilters).forEach((key) => {
        const value = apiFilters[key];
        if (
          value &&
          value !== "" &&
          value !== "all" &&
          key !== "companyId" &&
          key !== "supplierId"
        ) {
          params.append(key, value);
        }
      });

      // Append all parameters to URL
      const queryString = params.toString();
      if (queryString) {
        url += `&${queryString}`;
      }

      console.log("API URL:", url); // Debug log

      const res = await apiService.getAll(url);

      if (res.data) {
        setRequests(res.data);
        setPageNumber(res.pageNumber);
        setPageSize(res.pageSize);
        setTotalPages(res.totalPages);
        setTotalRecords(res.totalRecords);
      }
    } catch (error) {
      console.error("Error fetching visa requests:", error);
      setRequests([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  const GetApplicants = async (visaRequestId) => {
    try {
      const res = await apiService.getAll(
        `${config.GETVISAAPPLICANTSBYREQUEST}/${visaRequestId}`
      );
      if (res) {
        setApplicantsList(res);
      }
    } catch (error) {
      console.error("Error fetching applicants:", error);
      setApplicantsList([]);
    }
  };

  const handleOpenApplicantModal = (request) => {
    console.log("Opening applicant modal with request:", request);
    console.log("VisaRequestId:", request?.visaRequestId);

    if (request && request.visaRequestId) {
      setSelectedVisaRequest(request);
      GetApplicants(request.visaRequestId);
      setShowApplicantModal(true);
    } else {
      console.error("Invalid request or missing visaRequestId");
    }
  };

  const handleCloseApplicantModal = () => {
    setShowApplicantModal(false);
    setSelectedVisaRequest(null);
    setApplicantsList([]);
  };

  const handleApplicantSuccess = () => {
    // Refresh the main data
    GetData(filters, pageNumber);
    // Refresh applicants list if modal is still open
    if (selectedVisaRequest) {
      GetApplicants(selectedVisaRequest.visaRequestId);
    }
  };

  useEffect(() => {
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;

    // Load companies and suppliers when component mounts
    getCompanies();
    getSuppliersList();

    if (isInitialMount.current) {
      isInitialMount.current = false;
      const defaultDates = getCurrentMonthDateRange();
      setFilters((prev) => ({
        ...prev,
        fromDate: defaultDates.fromDate,
        toDate: defaultDates.toDate,
      }));
      GetData(
        {
          ...filters,
          fromDate: formatDate(defaultDates.fromDate),
          toDate: formatDate(defaultDates.toDate),
        },
        1
      );
      return;
    }

    const debounceTimer = setTimeout(() => {
      GetData(filters, 1);
    }, 350);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [user, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle date changes for DatePicker
  const handleDateChange = (date, fieldName) => {
    setFilters((prev) => ({
      ...prev,
      [fieldName]: date,
    }));
  };

  const resetFilters = () => {
    const defaultDates = getCurrentMonthDateRange();
    setFilters({
      visaReferenceNo: "",
      customerName: "",
      visaName: "",
      countryName: "",
      visaRequestStatus: "",
      paymentStatus: "",
      fromDate: defaultDates.fromDate,
      toDate: defaultDates.toDate,
      companyId: "",
      supplierId: "",
    });
    setPageNumber(1);
  };

  useEffect(() => {
    if (showSupplierModal && selectedRequest) {
      GetSuppliers("");
    }
  }, [showSupplierModal, selectedRequest]);

  const handleOpenSupplierModal = (request) => {
    if (request && request.visaRequestId) {
      setSelectedRequest(request);
      setShowSupplierModal(true);
      setSupplierSearch("");
    } else {
      console.error("Invalid request object passed to handleOpenSupplierModal");
    }
  };

  const handleCloseSupplierModal = () => {
    setShowSupplierModal(false);
    setSupplierSearch("");
    setSelectedSupplier(null);
  };

  const handleCloseNoApplicantsModal = () => {
    setShowNoApplicantsModal(false);
  };

  const handleAddApplicantsFromWarning = () => {
    setShowNoApplicantsModal(false);
    if (selectedRequest && selectedRequest.visaRequestId) {
      handleOpenApplicantModal(selectedRequest);
    } else {
      console.error("Selected request or visaRequestId is undefined");
    }
  };

  // New functions for markup and status popups
  const handleOpenMarkupPopup = (request) => {
    setSelectedRequestForAction(request);
    setShowMarkupPopup(true);
  };

  const handleOpenStatusPopup = (request) => {
    setSelectedRequestForAction(request);
    setShowStatusPopup(true);
  };

  const handleClosePopups = () => {
    setShowMarkupPopup(false);
    setShowStatusPopup(false);
    setSelectedRequestForAction(null);
  };

  // These functions will be called by the popups after successful API calls
  const handleMarkupSaved = () => {
    GetData(filters, pageNumber); // Refresh data
    handleClosePopups();
  };

  const handleStatusUpdated = () => {
    GetData(filters, pageNumber); // Refresh data
    handleClosePopups();
  };

  const GetSuppliers = async (searchQuery) => {
    if (!selectedRequest) return;

    try {
      let url = `${config.GETSUPPLIERVISAPRICESBYVISAID}/${selectedRequest.visaId}`;
      if (searchQuery) {
        url += `?search=${encodeURIComponent(searchQuery)}`;
      }

      const res = await apiService.getAll(url);
      setSuppliers(res);
    } catch (error) {
      console.error("Error fetching supplier visa prices:", error);
      setSuppliers([]);
    }
  };

  const handleSupplierSelection = (supplier) => {
    setSelectedSupplier(supplier);

    // Check if selectedRequest exists and has visaRequestId
    if (selectedRequest && selectedRequest.visaRequestId) {
      if (selectedRequest.noOfApplicants > 0) {
        setShowSupplierModal(false);
        setShowConfirmationModal(true);
      } else {
        setShowNoApplicantsModal(true);
      }
    } else {
      console.error("Selected request is undefined");
    }
  };

  const handleSendToSupplier = async () => {
    if (!selectedRequest || !selectedSupplier) return;

    setSendingToSupplier(selectedSupplier.supplierId);
    try {
      const url = `${config.SENDVISATOSUPPLIER}?visaRequestId=${selectedRequest.visaRequestId}&supplierId=${selectedSupplier.supplierId}`;

      await apiService.create(url);
      GetData(filters, pageNumber);
      setShowConfirmationModal(false);
      setShowSupplierModal(false);
    } catch (error) {
      console.error("Error sending visa request to supplier:", error);
    } finally {
      setSendingToSupplier(null);
      setSelectedSupplier(null);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmationModal(false);
    setShowSupplierModal(true);
  };

  const getStatusBadgeClass = (status) => {
    if (!status) return "badge bg-secondary";

    switch (status.toUpperCase()) {
      case "APPROVED":
        return "badge bg-success";
      case "PENDING":
      case "PROCESSING":
      case "DOCUMENT_PENDING":
        return "badge bg-warning text-dark";
      case "REJECTED":
        return "badge bg-danger";
      case "SUBMITTED":
      case "NEW":
        return "badge bg-info";
      case "DOCUMENTS_MISSING":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  const getPaymentBadgeClass = (status) => {
    if (!status) return "badge bg-secondary";

    switch (status.toUpperCase()) {
      case "PAID":
        return "badge bg-success";
      case "PENDING":
        return "badge bg-warning text-dark";
      case "FAILED":
      case "CANCELLED":
        return "badge bg-danger";
      case "REFUNDED":
        return "badge bg-info";
      default:
        return "badge bg-secondary";
    }
  };

  // Pagination functions
  const GetPageData = (number) => {
    if (number > 0 && number <= totalPages) {
      setPageNumber(number);
      GetData(filters, number);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, pageNumber - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <>
        {startPage > 1 && (
          <>
            <li className="page-item" onClick={() => GetPageData(1)}>
              <Link to="#" className="page-link">
                1
              </Link>
            </li>
            {startPage > 2 && (
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            )}
          </>
        )}

        {pageNumbers.map((number) => (
          <li
            className={`page-item ${pageNumber === number ? "active" : ""}`}
            key={number}
            onClick={() => GetPageData(number)}
          >
            <Link to="#" className="page-link">
              {number}
            </Link>
          </li>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            )}
            <li className="page-item" onClick={() => GetPageData(totalPages)}>
              <Link to="#" className="page-link">
                {totalPages}
              </Link>
            </li>
          </>
        )}
      </>
    );
  };

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <AdminTabs pageName="CLIENT VISA REQUESTS" />
              </div>
              <div className="card-body">
                <div className="row g-2 mb-3">
                  {/* From Date with DatePicker */}
                  <div className="col-xl-2 col-md-4">
                    <label className="form-label">From Date</label>
                    <DatePicker
                      selected={filters.fromDate}
                      onChange={(date) => handleDateChange(date, "fromDate")}
                      customInput={<CustomDateInput />}
                      dateFormat="dd-MMM-yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={50}
                      scrollableYearDropdown
                      className="form-control"
                    />
                  </div>

                  {/* To Date with DatePicker */}
                  <div className="col-xl-2 col-md-4">
                    <label className="form-label">To Date</label>
                    <DatePicker
                      selected={filters.toDate}
                      onChange={(date) => handleDateChange(date, "toDate")}
                      customInput={<CustomDateInput />}
                      dateFormat="dd-MMM-yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={50}
                      scrollableYearDropdown
                      className="form-control"
                    />
                  </div>

                  <div className="col-xl-2 col-md-4">
                    <label className="form-label">Reference No</label>
                    <input
                      type="text"
                      className="form-control form-control-md"
                      placeholder="Reference No"
                      name="visaReferenceNo"
                      value={filters.visaReferenceNo}
                      onChange={handleFilterChange}
                    />
                  </div>

                  <div className="col-xl-2 col-md-4">
                    <label className="form-label">Customer Name</label>
                    <input
                      type="text"
                      className="form-control form-control-md"
                      placeholder="Customer Name"
                      name="customerName"
                      value={filters.customerName}
                      onChange={handleFilterChange}
                    />
                  </div>

                  <div className="col-xl-2 col-md-4">
                    <label className="form-label">Visa Name</label>
                    <input
                      type="text"
                      className="form-control form-control-md"
                      placeholder="Visa Name"
                      name="visaName"
                      value={filters.visaName}
                      onChange={handleFilterChange}
                    />
                  </div>

                  <div className="col-xl-2 col-md-4">
                    <label className="form-label">Country</label>
                    <input
                      type="text"
                      className="form-control form-control-md"
                      placeholder="Country"
                      name="countryName"
                      value={filters.countryName}
                      onChange={handleFilterChange}
                    />
                  </div>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-xl-2 col-md-4">
                    <label className="form-label">Visa Status</label>
                    <select
                      className="form-select form-select-md"
                      name="visaRequestStatus"
                      value={filters.visaRequestStatus}
                      onChange={handleFilterChange}
                    >
                      <option value="all">All Visa Statuses</option>
                      <option value="New">New</option>
                      <option value="PENDING">Pending</option>
                      <option value="PROCESSING">Processing</option>
                      <option value="SUBMITTED">Submitted</option>
                      <option value="APPROVED">Approved</option>
                      <option value="REJECTED">Rejected</option>
                      <option value="DOCUMENTS_MISSING">
                        Documents Missing
                      </option>
                      <option value="DOCUMENT_PENDING">Document Pending</option>
                    </select>
                  </div>

                  <div className="col-xl-2 col-md-4">
                    <label className="form-label">Payment Status</label>
                    <select
                      className="form-select form-select-md"
                      name="paymentStatus"
                      value={filters.paymentStatus}
                      onChange={handleFilterChange}
                    >
                      <option value="all">All Payment Statuses</option>
                      <option value="PAID">Successful</option>
                      <option value="CANCELLED">Cancelled</option>
                      <option value="PENDING">Pending</option>
                    </select>
                  </div>

                  {/* Company Dropdown */}
                  <div className="col-xl-2 col-md-4">
                    <label className="form-label">Company</label>
                    <select
                      className="form-select form-select-md"
                      name="companyId"
                      value={filters.companyId}
                      onChange={handleFilterChange}
                      disabled={companyLoading}
                    >
                      <option value="">All Companies</option>
                      {companies.map((company) => (
                        <option
                          key={company.companyId}
                          value={company.companyId}
                        >
                          {company.companyName}
                        </option>
                      ))}
                    </select>
                    {companyLoading && (
                      <div className="small text-muted">
                        Loading companies...
                      </div>
                    )}
                  </div>

                  {/* Supplier Dropdown */}
                  <div className="col-xl-2 col-md-4">
                    <label className="form-label">Supplier</label>
                    <select
                      className="form-select form-select-md"
                      name="supplierId"
                      value={filters.supplierId}
                      onChange={handleFilterChange}
                      disabled={supplierLoading}
                    >
                      <option value="">All Suppliers</option>
                      {suppliersList.map((supplier) => (
                        <option
                          key={supplier.companyId}
                          value={supplier.companyId}
                        >
                          {supplier.companyName}
                        </option>
                      ))}
                    </select>
                    {supplierLoading && (
                      <div className="small text-muted">
                        Loading suppliers...
                      </div>
                    )}
                  </div>

                  <div className="col-xl-2 col-md-4 d-flex align-items-end">
                    <button
                      className="btn btn-md btn-danger"
                      onClick={resetFilters}
                    >
                      <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                      Reset
                    </button>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading visa Requests...</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-centered table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Reference No</th>
                            <th>Visa</th>
                            <th>Customer</th>
                            <th>Company</th>
                            <th>Supplier</th>
                            <th>Supplier Price</th>
                            <th>Client Price</th>
                            {/* <th>Service Fee</th> */}
                            {/* <th>MarkUP</th>
                            <th>Customer Price</th> */}
                            <th>Visa Status</th>
                            <th>Customer Payment</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {requests && requests.length > 0 ? (
                            requests.map((request) => (
                              <tr key={request.visaRequestId}>
                                <td>{request.visaReferenceNo}</td>

                                <td
                                  style={{
                                    whiteSpace: "normal",
                                    wordWrap: "break-word",
                                    maxWidth: "150px",
                                  }}
                                >
                                  <div className="fw-semibold">
                                    {request.visaName}
                                  </div>
                                  <small className="text-muted">
                                    {request.countryName}
                                  </small>
                                </td>
                                <td>
                                  <div>{request.applicantName || "-"}</div>
                                  <div className="text-danger">
                                    No. of Applicants:{" "}
                                    {request.noOfApplicants || 0}
                                  </div>
                                </td>

                                <td>
                                  <div className="fw-semibold text-primary">
                                    {request.companyName || "-"}
                                  </div>
                                </td>

                                <td>
                                  <div className="fw-semibold text-info">
                                    {request.supplierName || "Not assigned"}
                                  </div>
                                </td>

                                <td>
                                  {request.supplierPrice ? (
                                    <span className="fw-bold text-primary">
                                      INR {request.supplierPrice}
                                    </span>
                                  ) : (
                                    <span className="fw-bold text-primary">
                                      -
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {request.totalPrice ? (
                                    <span className="fw-bold text-primary">
                                      INR {request.totalPrice}
                                    </span>
                                  ) : (
                                    <span className="fw-bold text-primary">
                                      -
                                    </span>
                                  )}
                                </td>

                                {/* <td>
                                  {request.serviceFee ? (
                                    <span className="fw-bold text-primary">
                                      INR {request.serviceFee}
                                    </span>
                                  ) : (
                                    <span className="fw-bold text-primary">
                                      -
                                    </span>
                                  )}
                                </td> */}

                                {/* <td>
                                  <div className="d-flex flex-column gap-1">
                                    {request.customerPrice ? (
                                      <span className="fw-bold text-success">
                                        INR {request.customerPrice}
                                      </span>
                                    ) : (
                                      <span className="fw-bold text-success">
                                        -
                                      </span>
                                    )}

                                    {request.supplierId &&
                                      request.supplierId !==
                                        "00000000-0000-0000-0000-000000000000" && (
                                        <span
                                          style={{
                                            cursor: "pointer",
                                            color: "#000",
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            textDecoration: "underline",
                                          }}
                                          onClick={() =>
                                            handleOpenMarkupPopup(request)
                                          }
                                        >
                                          Markup
                                        </span>
                                      )}
                                  </div>
                                </td> */}

                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span
                                      className={getStatusBadgeClass(
                                        request.visaRequestStatus
                                      )}
                                    >
                                      {request.visaRequestStatus}
                                    </span>

                                    {request.visaRequestStatus && (
                                      <span
                                        style={{
                                          cursor: "pointer",
                                          color: "#000",
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          marginTop: "5px",
                                          textDecoration: "underline",
                                        }}
                                        onClick={() =>
                                          handleOpenStatusPopup(request)
                                        }
                                      >
                                        Update
                                      </span>
                                    )}
                                  </div>
                                </td>

                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span
                                      className={getPaymentBadgeClass(
                                        request.paymentStatus
                                      )}
                                    >
                                      {request.paymentStatus}
                                    </span>

                                    {request.paymentStatus && (
                                      <span
                                        style={{
                                          cursor: "pointer",
                                          color: "#000",
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          marginTop: "5px",
                                          textDecoration: "underline",
                                        }}
                                        onClick={() =>
                                          handleOpenStatusPopup(request)
                                        }
                                      >
                                        Update
                                      </span>
                                    )}
                                  </div>
                                </td>

                                <td>
                                  <div className="d-flex gap-1">
                                    <Link
                                      to={`/request/${request.visaRequestId}`}
                                      className="btn btn-sm btn-primary p-1"
                                      title="View"
                                      style={{ width: "30px", height: "30px" }}
                                    >
                                      <i className="ri-eye-line"></i>
                                    </Link>

                                    <button
                                      className="btn btn-sm btn-info p-1"
                                      title="Add Applicants"
                                      onClick={() =>
                                        handleOpenApplicantModal(request)
                                      }
                                      style={{ width: "30px", height: "30px" }}
                                    >
                                      <i className="ri-user-add-line"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-success p-1"
                                      title="Assign Supplier"
                                      onClick={() =>
                                        handleOpenSupplierModal(request)
                                      }
                                      style={{ width: "30px", height: "30px" }}
                                    >
                                      <i className="ri-store-2-line"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="11" className="text-center py-4">
                                No visa requests found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {requests.length > 0 && (
                      <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                        <div className="flex-shrink-0">
                          <div className="text-muted">
                            Showing{" "}
                            <span className="fw-semibold">
                              {requests.length}
                            </span>{" "}
                            of{" "}
                            <span className="fw-semibold">{totalRecords}</span>{" "}
                            Results
                          </div>
                        </div>
                        <ul className="pagination pagination-separated pagination-sm mb-0">
                          <li
                            className={`page-item ${
                              pageNumber === 1 ? "disabled" : ""
                            }`}
                            onClick={() =>
                              pageNumber > 1 && GetPageData(pageNumber - 1)
                            }
                          >
                            <Link to="#" className="page-link">
                              Previous
                            </Link>
                          </li>
                          {renderPageNumbers()}
                          <li
                            className={`page-item ${
                              pageNumber === totalPages ? "disabled" : ""
                            }`}
                            onClick={() =>
                              pageNumber < totalPages &&
                              GetPageData(pageNumber + 1)
                            }
                          >
                            <Link to="#" className="page-link">
                              Next
                            </Link>
                          </li>
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Supplier Modal */}
     
{showSupplierModal && (
  <div
    className="modal fade show d-block"
    tabIndex="-1"
    role="dialog"
    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
  >
    <div className="modal-dialog modal-lg" role="document">
      <div className="modal-content">
        <div className="modal-header bg-light">
          <div>
            <h5 className="modal-title mb-1">
              Send to Supplier - {selectedRequest?.countryName}
            </h5>
            {selectedRequest?.visaName && (
              <div className="fw-bold text-primary fs-6">
                {selectedRequest.visaName}
              </div>
            )}
            <div className="mt-2">
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <i className="ri-user-line me-2 text-muted"></i>
                    <span className="fw-semibold">Applicants:</span>
                    <span className="ms-2 text-danger fw-bold">
                      {selectedRequest?.noOfApplicants || 0}
                    </span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <i className="ri-building-line me-2 text-muted"></i>
                    <span className="fw-semibold">Currently assigned:</span>
                    {selectedRequest?.supplierName ? (
                      <span className="ms-2 badge bg-success">
                        {selectedRequest.supplierName}
                      </span>
                    ) : (
                      <span className="ms-2 text-muted">Not assigned</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={handleCloseSupplierModal}
          ></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-info mb-3">
            <div className="d-flex align-items-center">
              <i className="ri-information-line me-2 fs-4"></i>
              <div>
                <strong>Note:</strong> This request is currently assigned to{" "}
                <span className="fw-bold text-primary">
                  {selectedRequest?.supplierName || "no supplier"}
                </span>
                . Select a new supplier below to reassign.
              </div>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Search Suppliers</label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Type supplier name, email, or phone..."
                value={supplierSearch}
                onChange={(e) => {
                  setSupplierSearch(e.target.value);
                  GetSuppliers(e.target.value);
                }}
              />
            </div>
          </div>

          <div
            className="table-responsive"
            style={{ maxHeight: "400px", overflowY: "auto" }}
          >
            <table className="table table-hover align-middle mb-0">
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "white",
                  zIndex: 1,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}
              >
                <tr>
                  <th style={{ width: "35%" }}>Supplier Details</th>
                  <th style={{ width: "25%" }}>Contact Information</th>
                  <th style={{ width: "20%" }}>Pricing</th>
                  <th style={{ width: "20%" }} className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {suppliers && suppliers.length > 0 ? (
                  suppliers.map((item) => {
                    // Check if this is the currently assigned supplier
                    const isCurrentSupplier = 
                      selectedRequest?.supplierId &&
                      (item.supplier?.supplierId || item.supplierId) &&
                      (item.supplier?.supplierId === selectedRequest.supplierId || 
                       item.supplierId === selectedRequest.supplierId);

                    return (
                      <tr
                        key={item.supplierVisaPriceId || item.supplier?.supplierId}
                        className={
                          isCurrentSupplier ? "table-success" : ""
                        }
                        style={{
                          backgroundColor: isCurrentSupplier ? '#d1e7dd' : 'transparent',
                        }}
                      >
                        {/* Supplier Details Column */}
                        <td>
                          <div className="d-flex align-items-start">
                            {isCurrentSupplier && (
                              <div className="me-2 mt-1">
                                <i className="ri-checkbox-circle-fill text-success fs-5"></i>
                              </div>
                            )}
                            <div className="flex-grow-1">
                              <div className="fw-bold fs-6 text-truncate">
                                {item.supplierName || item.supplier?.supplierName}
                              </div>
                              <div className="text-muted small">
                                {item.visaName}
                              </div>
                              {isCurrentSupplier && (
                                <div className="mt-1">
                                  <span className="badge bg-success">
                                    <i className="ri-check-line me-1"></i>
                                    Currently Assigned
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Contact Information Column */}
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <div className="d-flex align-items-center">
                              <i className="ri-phone-line text-success me-2 fs-5"></i>
                              <span className="small text-truncate">
                                {item.phoneNumber || item.supplier?.phoneNumber || "N/A"}
                              </span>
                            </div>
                            <div className="d-flex align-items-center">
                              <i className="ri-mail-line text-danger me-2 fs-5"></i>
                              <div
                                className="small text-truncate"
                                style={{
                                  maxWidth: "180px",
                                }}
                                title={item.email || item.supplier?.email}
                              >
                                {item.email || item.supplier?.email || "N/A"}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Pricing Column */}
                        <td>
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex align-items-center justify-content-between">
                              <span className="small fw-semibold">Adult:</span>
                              <span className="badge bg-success">
                                ₹{item.adultPrice || "0"}
                              </span>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <span className="small fw-semibold">Child:</span>
                              <span className="badge bg-info">
                                ₹{item.childPrice || "0"}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Action Column */}
                        <td className="text-center">
                          {isCurrentSupplier ? (
                            <button
                              className="btn btn-sm btn-success px-3"
                              disabled
                              style={{ 
                                cursor: "not-allowed",
                                opacity: 0.8,
                                pointerEvents: "none",
                                fontWeight: "bold",
                                minWidth: "100px"
                              }}
                            >
                              <i className="ri-check-double-line me-1"></i>
                              Selected
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-primary px-3"
                              onClick={() => handleSupplierSelection(item)}
                              style={{ 
                                fontWeight: "bold",
                                minWidth: "100px"
                              }}
                            >
                              <i className="ri-send-plane-line me-1"></i>
                              Select
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      <div className="text-muted">
                        <i className="ri-search-line fs-3 mb-2 d-block"></i>
                        No suppliers found matching your search
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="modal-footer">
          <div className="me-auto">
            {selectedRequest?.supplierName && (
              <div className="text-muted small">
                <i className="ri-information-line me-1"></i>
                Currently assigned to: <span className="fw-bold text-success">{selectedRequest.supplierName}</span>
              </div>
            )}
          </div>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleCloseSupplierModal}
          >
            <i className="ri-close-line me-1"></i>
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
)}
      {/* No Applicants Modal */}
      {showNoApplicantsModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-sm" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Applicants</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseNoApplicantsModal}
                ></button>
              </div>
              <div className="modal-body text-center">
                <div className="alert alert-warning">
                  <i className="ri-error-warning-line fs-2 text-warning"></i>
                  <p className="mt-2 mb-0">
                    Applicants are not added. Please add applicants first.
                  </p>
                </div>
              </div>
              <div className="modal-footer justify-content-center">
                <button
                  className="btn btn-primary"
                  onClick={handleAddApplicantsFromWarning}
                >
                  Add Applicants
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleCloseNoApplicantsModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send to Supplier</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelConfirmation}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <p>
                    Please confirm you want to send this visa request to the
                    supplier:
                  </p>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Visa Type:</strong>
                  </div>
                  <div className="col-md-6">{selectedRequest?.visaName}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Country:</strong>
                  </div>
                  <div className="col-md-6">{selectedRequest?.countryName}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Applicants:</strong>
                  </div>
                  <div className="col-md-6">
                    {selectedRequest?.noOfApplicants}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Supplier:</strong>
                  </div>
                  <div className="col-md-6">
                    {selectedSupplier?.supplierName}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <strong>Supplier Email:</strong>
                  </div>
                  <div className="col-md-6">{selectedSupplier?.email}</div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleCancelConfirmation}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendToSupplier}
                  disabled={sendingToSupplier !== null}
                >
                  {sendingToSupplier ? "Sending..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Markup Popup */}
      {showMarkupPopup && selectedRequestForAction && (
        <MarkupModal
          visaRequest={selectedRequestForAction}
          onSave={handleMarkupSaved}
          onClose={handleClosePopups}
        />
      )}

      {/* Status Update Popup */}
      {showStatusPopup && selectedRequestForAction && (
        <StatusModal
          visaRequest={selectedRequestForAction}
          onSave={handleStatusUpdated}
          onClose={handleClosePopups}
        />
      )}

      {/* Applicant Modal */}
      {showApplicantModal && selectedVisaRequest && (
        <ApplicantForm
          show={showApplicantModal}
          onHide={handleCloseApplicantModal}
          visaRequestId={selectedVisaRequest.visaRequestId}
          onSuccess={handleApplicantSuccess}
          applicantsList={applicantsList}
        />
      )}
    </div>
  );
};

export default ClientsVisaRequests;
